import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AuthLayout } from "../catalyst/auth-layout";
import { Button } from "../catalyst/button";
import { ErrorMessage, Field } from "../catalyst/fieldset";
import { Heading } from "../catalyst/heading";
import InputWithError from "../catalyst/custom/inputWithError";
import { Strong, Text, TextLink } from "../catalyst/text";
import Spinning from "../components/spinning";
import { Logo } from "../logo";
import CheckCircleIcon from "@heroicons/react/24/solid/CheckCircleIcon";
import config from "../config";

const ForgotPassword = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "forgotPassword" });
  const [formData, setFormData] = useState({ email: "" });
  const [errors, setErrors] = useState({});
  const [errResponse, setErrResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentSuccessfully, setSentSuccessfully] = useState(false);

  const validate = ({ email }) => {
    if (!email) return { email: t("emailRequired") };
    if (!/\S+@\S+\.\S+/.test(email)) return { email: t("emailInvalid") };
    return {};
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formDataObj = new FormData(e.currentTarget);
    const newData = { email: formDataObj.get("email") };
    const validationErrors = validate(newData);
    setFormData(newData);

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      const res = await fetch(`${config.apiUrl}/user/resetPassword`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newData),
      });

      if (!res.ok) {
        const msg = res.status === 400 ? await res.text() : "errorSending";
        throw new Error(t(msg));
      }

      setErrResponse(t("sentMessage"));
      setSentSuccessfully(true);
    } catch (err) {
      setErrResponse(err.message);
      console.error("Error:", err);
    } finally {
      setSubmitting(false);
      setErrors({});
    }
  };

  const ForgotForm = () => (
    <form
      onSubmit={handleSubmit}
      className="grid w-full max-w-sm grid-cols-1 gap-8"
    >
      <div className="flex flex-col items-center justify-center space-y-4">
        <Logo
          className="h-6 text-zinc-950 dark:text-white forced-colors:text-[CanvasText]"
          redirectHome
        />
        <Heading>{t("title")}</Heading>
        <Text>{t("titleNote")}</Text>
      </div>

      <InputWithError
        error={errors.email}
        type="email"
        name="email"
        label={t("email")}
        onChange={() => {}}
      />

      <div className="flex items-center justify-between">
        <Text>
          <TextLink href="login">
            <Strong>Back to login?</Strong>
          </TextLink>
        </Text>
      </div>

      {errResponse && (
        <Field>
          <ErrorMessage>{errResponse}</ErrorMessage>
        </Field>
      )}

      <Button type="submit" className="w-full cursor-pointer">
        {submitting ? <Spinning /> : t("resetPassword")}
      </Button>

      <div className="flex items-center justify-between">
        <Text className="ml-auto">
          {t("dontHaveAnAccount")}{" "}
          <TextLink href="signup">
            <Strong>{t("signup")}</Strong>
          </TextLink>
        </Text>
      </div>
    </form>
  );

  const ResetConfirmation = () => (
    <div className="grid w-full max-w-sm grid-cols-1 gap-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Logo
          className="h-6 text-zinc-950 dark:text-white forced-colors:text-[CanvasText]"
          redirectHome
        />
        <Heading>Password Reset email sent</Heading>
        <CheckCircleIcon
          className="h-30 w-30 text-green-500"
          aria-hidden="true"
        />
        <Text>
          We’ve emailed you a link to reset your password to{" "}
          <Strong>{formData.email}</Strong>.
        </Text>
        <Text>
          If it hasn’t arrived, check your spam folder or try sending the email
          again.
        </Text>
        <Link to={config.loginPath}>
          <Button className="cursor-pointer">Back to login</Button>
        </Link>
      </div>
    </div>
  );

  return (
    <AuthLayout>
      {sentSuccessfully ? <ResetConfirmation /> : <ForgotForm />}
    </AuthLayout>
  );
};

export default ForgotPassword;
